import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ConfirmDialog, ArchiveConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteCommittee, fetchCommittees } from '../committeesApi';
import { archiveEntity } from '../archiveApi';
import { committeeTypeLabel } from '../committeeFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { useBranchFilter } from '../branchFilter';
import './BranchesPage.css';
import './BoardPage.css';

const PAGE_SIZE = 8;

export default function CommitteesPage() {
  const { language, direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { selectedBranch: globalBranch } = useBranchFilter();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchCommittees();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('committees.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveBranch = globalBranch !== 'all' ? globalBranch : branchFilter;

  const filtered = useMemo(() => {
    return rows.filter((x) => {
      const q = search.trim().toLowerCase();
      const qMatch = !q
        || x.name.toLowerCase().includes(q)
        || (x.chairmanName || '').toLowerCase().includes(q)
        || (x.branch || '').toLowerCase().includes(q);
      const bMatch = effectiveBranch === 'all' || x.branch === effectiveBranch;
      const tMatch = typeFilter === 'all' || x.type === typeFilter;
      return qMatch && bMatch && tMatch;
    });
  }, [rows, search, effectiveBranch, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCount = rows.filter((x) => x.isActive).length;

  const openDelete = (row) => { setDeleteTarget(row); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCommittee(deleteTarget.id);
      setRows((prev) => prev.map((x) => (x.id === deleteTarget.id ? { ...x, isActive: false } : x)));
      setConfirmOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('committees.deleteFailed'));
      setConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="board" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('committees.title')}</span>
            <span className="list-page-card__sub">{t('committees.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button className="btn btn--ghost" onClick={() => navigate(`${basePath}/archive?type=Committee`)}><Icon name="archive" size={14} /> {t('nav.archive')}</button>
            <button className="btn btn--primary" onClick={() => navigate(`${basePath}/committees/new`)}><Icon name="plus" size={14} color="#fff" /> {t('committees.add')}</button>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('committees.loading')}</div>}

      {!loading && (
        <>
          <div className="page__stats">
            <div className="page__stat-chip page__stat-chip--total"><Icon name="board" size={13} /> {t('common.total')}: {rows.length}</div>
            <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t('committees.active')}: {activeCount}</div>
            <div className="page__stat-chip page__stat-chip--new"><Icon name="x-circle" size={13} /> {t('committees.inactive')}: {rows.length - activeCount}</div>
          </div>

          <div className="page__filters">
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
              <input className="page__search-input" placeholder={t('committees.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="page__filter-select" value={branchFilter} onChange={(e) => { setBranchFilter(e.target.value); setPage(1); }}>
              <option value="all">{t('committees.allBranches')}</option>
              {Array.from(new Set(rows.map((x) => x.branch))).filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="page__filter-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="all">{t('committees.allTypes')}</option>
              <option value="permanent">{committeeTypeLabel('permanent', language)}</option>
              <option value="temporary">{committeeTypeLabel('temporary', language)}</option>
            </select>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('committees.colName')}</th>
                  <th>{t('committees.colType')}</th>
                  <th>{t('committees.colChairman')}</th>
                  <th>{t('committees.colMembers')}</th>
                  <th>{t('committees.colBranch')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><div className="empty-state__title">{t('committees.noMatch')}</div><div className="empty-state__sub">{t('committees.noMatchSub')}</div></div></td></tr>
                ) : paginated.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td><strong style={{ color: '#00334a' }}>{item.name}</strong></td>
                    <td>{committeeTypeLabel(item.type, language)}</td>
                    <td>{item.chairmanName || '—'}</td>
                    <td>{item.membersCount}</td>
                    <td>{item.branch || '—'}</td>
                    <td><span className={`users-status users-status--${item.isActive ? 'active' : 'inactive'}`}>{item.isActive ? t('committees.active') : t('committees.inactive')}</span></td>
                    <td>
                      <div className="td-actions">
                        <button className="action-btn action-btn--view" onClick={() => navigate(`${basePath}/committees/${item.id}`)}><Icon name="eye" size={14} /></button>
                        <button className="action-btn action-btn--edit" onClick={() => navigate(`${basePath}/committees/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                        <button className="action-btn action-btn--archive" title={t('archive.title')} onClick={() => setArchiveConfirmId(item.id)}><Icon name="archive" size={14} /></button>
                        <button className="action-btn action-btn--delete" onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > PAGE_SIZE && (
              <div className="pagination">
                <span>{t('common.showing')} {Math.min(filtered.length, (page - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, page * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
                <div className="pagination__pages">
                  <button className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => <button key={p} className={`pagination__btn${page === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
                  <button className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name}
      />

      <ArchiveConfirmDialog
        open={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        onConfirm={async () => {
          if (!archiveConfirmId) return;
          setArchiving(true);
          try {
            await archiveEntity('Committee', archiveConfirmId);
            setRows((p) => p.filter((r) => r.id !== archiveConfirmId));
          } catch { /* ignore */ }
          setArchiving(false);
          setArchiveConfirmId(null);
        }}
        itemName={rows.find((r) => r.id === archiveConfirmId)?.name}
        title={t('archive.title')}
        confirmLabel={t('archive.title')}
        cancelLabel={t('common.cancel')}
        loading={archiving}
      />
    </div>
  );
}
